import {Alert, Box, Snackbar} from '@mui/material';
import {CloseIcon, ErrorNotification} from 'assets/icons';
import {FC, useState} from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {
  snackbarAlertContent,
  snackbarErrorAlert,
} from 'styles/MUIStyles/snackbars';
import {useAppDispatch, useAppSelector} from 'store/store';

import ButtonWithProgress from 'components/ButtonWithProgress/ButtonWithProgress';
import {ErrorMessage} from '@hookform/error-message';
import {FORGOT_PASSWORD_PAGE_PATH} from 'router/constants';
import {ISnackbarState} from 'store/models/common';
import eyeOff from 'assets/icons/eyeOff.svg';
import eyeOn from 'assets/icons/eyeOn.svg';
import {loginButton} from 'styles/MUIStyles/buttons';
import {mainPagesByRole} from 'core/constants';
import {setEmail} from 'store/slices/authSlice';
import styles from './Login.module.scss';
import {useForm} from 'react-hook-form';
import {useLoginMutation} from 'store/slices/profile';
import {useThemeMode} from 'theme/ThemeModeProvider';

interface IFormInput {
  email: string;
  password: string;
}

interface IErrorResponse {
  status?: number;
  data?: {
    errors?: Record<string, string[]>;
  };
}

const Login: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuth = useAppSelector((s) => s.auth.isAuth);
  const role = useAppSelector((s) => s.auth.role);
  const {resolved: resolvedTheme} = useThemeMode();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<IFormInput>({defaultValues: {email: '', password: ''}});

  const [login, {isLoading}] = useLoginMutation();

  const [errorSnackbar, setErrorSnackbar] = useState<ISnackbarState>({
    open: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSnackbarClose = () => {
    setErrorSnackbar({...errorSnackbar, open: false});
    setErrorMessage(null);
  };

  const togglePasswordVisibility = () => setIsPasswordVisible((prev) => !prev);

  const onSubmit = async (formData: IFormInput) => {
    // сохраняем email для страницы OTP
    dispatch(setEmail(formData.email));

    try {
      // если backend возвращает 200 – ждём только data
      await login(formData).unwrap();

      // всегда переходим на ввод OTP
      navigate('/user/otp-code');
    } catch (err) {
      const error = err as IErrorResponse;
      console.log(error);

      if (error?.status === 400 || error?.status === 404) {
        console.log(error);
        const fieldErrors = error.data as any[];
        if (fieldErrors) {
          console.log(fieldErrors[0].errors);
          for (const errorMessage of Object.values(fieldErrors[0].errors)) {
            if (errorMessage) {
              // @ts-ignore
              setErrorMessage(errorMessage[0] ?? 'Unknown error');
              setErrorSnackbar({
                vertical: 'top',
                horizontal: 'center',
                open: true,
              });
            }
          }
        }
      }
    }
  };

  if (isAuth && role) {
    return <Navigate to={`/${mainPagesByRole[role]}`} replace />;
  }

  return (
    <div className={styles.loginPageContainer}>
      <div className="container">
        <div className={styles.loginFormContainer}>
          <div className={styles.loginLogoContainer}>
            <img
              src={resolvedTheme === 'dark' ? '/UCS_LOGO_dark.png' : '/UCS_LOGO.png'}
              alt="UCS"
            />
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <h2 className={styles.loginTitle}>Log In</h2>

            {/* email */}
            <div className={styles.loginInputContainer}>
              <label htmlFor="login">Email</label>
              <div>
                <input
                  {...register('email', {required: 'This field is required'})}
                  type="text"
                  id="login"
                  placeholder="Enter login"
                  className={
                    errors.email
                      ? `${styles.loginInput} ${styles.loginInputError}`
                      : styles.loginInput
                  }
                />
                <ErrorMessage
                  errors={errors}
                  name="email"
                  render={({message}) => (
                    <span className={styles.loginErrorMessage}>{message}</span>
                  )}
                />
              </div>
            </div>

            {/* password */}
            <div className={styles.loginPasswordContainer}>
              <label htmlFor="password">Password</label>
              <div>
                <input
                  {...register('password', {
                    required: 'This field is required',
                  })}
                  type={isPasswordVisible ? 'text' : 'password'}
                  id="password"
                  placeholder="Enter password"
                  className={
                    errors.password
                      ? `${styles.passwordInput} ${styles.passwordInputError}`
                      : styles.passwordInput
                  }
                />
                <ErrorMessage
                  errors={errors}
                  name="password"
                  render={({message}) => (
                    <span className={styles.passwordErrorMessage}>
                      {message}
                    </span>
                  )}
                />
                <img
                  src={isPasswordVisible ? eyeOn : eyeOff}
                  alt={isPasswordVisible ? 'hide password' : 'show password'}
                  onClick={togglePasswordVisibility}
                  className={
                    errors.password
                      ? `${styles.passwordInputEye} ${styles.passwordInputEyeError}`
                      : styles.passwordInputEye
                  }
                />
              </div>
            </div>

            <ButtonWithProgress
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              sx={loginButton}
            >
              Log In
            </ButtonWithProgress>

            <div className={styles.forgotPasswordContainer}>
              <span
                onClick={() => navigate(`/${FORGOT_PASSWORD_PAGE_PATH}`)}
                className={styles.forgotPassword}
              >
                Forgot password?
              </span>
            </div>
          </form>
        </div>
      </div>

      {errorMessage && (
        <Snackbar
          anchorOrigin={{
            vertical: errorSnackbar.vertical,
            horizontal: errorSnackbar.horizontal,
          }}
          autoHideDuration={3500}
          open={errorSnackbar.open}
          onClose={handleSnackbarClose}
          key="createManifestTaskError"
        >
          <Alert icon={false} sx={snackbarErrorAlert}>
            <Box sx={snackbarAlertContent}>
              <ErrorNotification />
              {errorMessage}
              <span onClick={handleSnackbarClose}>
                <CloseIcon />
              </span>
            </Box>
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default Login;
